// FILE: src/pages/Training/TrainingPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { trainingAPI, caregiversAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Training.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';

function getStatus(expDate) {
  if (!expDate) return { label:'No Expiry', cls:'badge-gray' };
  const days = Math.ceil((new Date(expDate) - new Date()) / 86400000);
  if (days < 0)  return { label:'Expired',        cls:'badge-danger',  days };
  if (days <= 30) return { label:`Expires in ${days}d`, cls:'badge-warning', days };
  return { label:'Active', cls:'badge-success', days };
}

export default function TrainingPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [records,    setRecords]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [expiring,   setExpiring]   = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [tabFilt,    setTabFilt]    = useState('all');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [delLoad,    setDelLoad]    = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [tRes, expRes, cgRes] = await Promise.all([
        trainingAPI.getAll(),
        trainingAPI.getExpiring(30),
        caregiversAPI.getAll(),
      ]);
      setRecords(tRes.data.data   || []);
      setFiltered(tRes.data.data  || []);
      setExpiring(expRes.data.data || []);
      setCaregivers(cgRes.data.data || []);
    } catch { toast.error('Failed to load training.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...records];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        (r.CaregiverName||'').toLowerCase().includes(q) ||
        (r.TrainingTitle||'').toLowerCase().includes(q) ||
        (r.TrainingType||'').toLowerCase().includes(q)
      );
    }
    if (tabFilt === 'expired')  data = data.filter(r => r.ExpirationDate && new Date(r.ExpirationDate) < new Date());
    if (tabFilt === 'expiring') data = data.filter(r => {
      if (!r.ExpirationDate) return false;
      const days = Math.ceil((new Date(r.ExpirationDate) - new Date()) / 86400000);
      return days >= 0 && days <= 30;
    });
    if (tabFilt === 'active')   data = data.filter(r => {
      if (!r.ExpirationDate) return true;
      return new Date(r.ExpirationDate) > new Date();
    });
    setFiltered(data);
  }, [search, tabFilt, records]);

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await trainingAPI.delete(deleting.ID);
      toast.success('Record deleted.');
      setDeleting(null); load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const expired  = records.filter(r => r.ExpirationDate && new Date(r.ExpirationDate) < new Date()).length;
  const active   = records.filter(r => !r.ExpirationDate || new Date(r.ExpirationDate) > new Date()).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Training & Certifications</h1>
          <p>{records.length} records · {expiring.length} expiring soon</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search caregiver, training title…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}}/>
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>➕ Add Training</button>
          )}
        </div>
      </div>

      {expiring.length > 0 && (
        <div className="training-alert-banner">
          <span style={{fontSize:'1.1rem'}}>⚠️</span>
          <div>
            <strong>{expiring.length} certification{expiring.length>1?'s':''} expiring within 30 days:</strong>{' '}
            {expiring.slice(0,3).map(e=>`${e.CaregiverName} (${e.TrainingTitle})`).join(', ')}
            {expiring.length > 3 ? ` +${expiring.length-3} more` : ''}
          </div>
        </div>
      )}

      <div className="stats-grid-4" style={{marginBottom:16}}>
        {[
          {label:'Total Records', value:records.length,   icon:'📚'},
          {label:'Active',        value:active,            icon:'✅'},
          {label:'Expiring Soon', value:expiring.length,  icon:'⚠️'},
          {label:'Expired',       value:expired,           icon:'❌'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="training-tab-row">
        {[{v:'all',l:'All'},{v:'active',l:'Active'},{v:'expiring',l:'Expiring Soon'},{v:'expired',l:'Expired'}].map(t=>(
          <button key={t.v} className={`training-tab${tabFilt===t.v?' active':''}`} onClick={()=>setTabFilt(t.v)}>{t.l}</button>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading training records…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📚</div>
            <div className="empty-state-text">{search||tabFilt!=='all' ? 'No records match.' : 'No training records yet.'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr><th>Caregiver</th><th>Training</th><th>Type</th><th>Completed</th><th>Expires</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(r=>{
                  const st = getStatus(r.ExpirationDate);
                  return (
                    <tr key={r.ID}>
                      <td className="bold">{r.CaregiverName||'—'}</td>
                      <td style={{color:'var(--gray-700)'}}>{r.TrainingTitle||'—'}</td>
                      <td><span className="badge badge-primary">{r.TrainingType||'General'}</span></td>
                      <td style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{fmtDate(r.CompletionDate)}</td>
                      <td style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{fmtDate(r.ExpirationDate)}</td>
                      <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          {canEdit && (
                            <>
                              <button className="btn btn-sm btn-secondary" onClick={()=>{ setEditing(r); setShowModal(true); }}>✏️</button>
                              <button className="btn btn-sm btn-danger"    onClick={()=>setDeleting(r)}>🗑️</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <TrainingFormModal record={editing} caregivers={caregivers}
          onClose={()=>setShowModal(false)} onSaved={()=>{ setShowModal(false); load(); }}/>
      )}
      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header"><span className="modal-title">🗑️ Delete Training</span><button className="modal-close" onClick={()=>setDeleting(null)}>✕</button></div>
            <div className="modal-body"><p style={{color:'var(--gray-600)'}}>Delete <strong>{deleting.TrainingTitle}</strong> for {deleting.CaregiverName}?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>{delLoad?'Deleting…':'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TrainingFormModal({ record, caregivers, onClose, onSaved }) {
  const isEdit = !!record;
  const [form, setForm]   = useState({
    CaregiverID:     record?.CaregiverID     || '',
    TrainingTitle:   record?.TrainingTitle   || '',
    TrainingType:    record?.TrainingType    || '',
    CompletionDate:  record?.CompletionDate  ? record.CompletionDate.split('T')[0]  : '',
    ExpirationDate:  record?.ExpirationDate  ? record.ExpirationDate.split('T')[0]  : '',
    CertificateNumber: record?.CertificateNumber || '',
    Notes:           record?.Notes           || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f,v) => setForm(p=>({...p,[f]:v}));

  const handleSave = async () => {
    if (!form.CaregiverID || !form.TrainingTitle) { toast.error('Caregiver and title are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await trainingAPI.update(record.ID,form); toast.success('Updated.'); }
      else        { await trainingAPI.create(form);           toast.success('Training record added.'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Save failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit?'✏️ Edit Training':'➕ Add Training Record'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Caregiver <span className="req">*</span></label>
              <select className="form-control" value={form.CaregiverID} onChange={e=>set('CaregiverID',e.target.value)}>
                <option value="">— Select —</option>
                {caregivers.map(c=><option key={c.UserID} value={c.UserID}>{c.FirstName} {c.LastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Training Type</label>
              <select className="form-control" value={form.TrainingType} onChange={e=>set('TrainingType',e.target.value)}>
                <option value="">— Select —</option>
                {['CPR','First Aid','HHA Certification','CNA Certification','Medication Management',
                  'Dementia Care','HIPAA','Background Check','Orientation','Other'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Training Title <span className="req">*</span></label>
            <input className="form-control" value={form.TrainingTitle} onChange={e=>set('TrainingTitle',e.target.value)} placeholder="e.g. CPR/AED Certification 2025"/>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Completion Date</label>
              <input className="form-control" type="date" value={form.CompletionDate} onChange={e=>set('CompletionDate',e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Expiration Date</label>
              <input className="form-control" type="date" value={form.ExpirationDate} onChange={e=>set('ExpirationDate',e.target.value)}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Certificate #</label>
              <input className="form-control" value={form.CertificateNumber} onChange={e=>set('CertificateNumber',e.target.value)} placeholder="Certificate number"/>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-control" value={form.Notes} onChange={e=>set('Notes',e.target.value)} placeholder="Optional notes"/>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'💾 Update':'✅ Add Record'}</button>
        </div>
      </div>
    </div>
  );
}